import router from '@/router';
import { dispose3DViewer, launchPlanViewer, launchViewer } from '@/scripts/js/forgeviewer';
import store from '@/store';
import HttpService from '@/utils/httpService';
import { getStreamingApi } from '@/utils/regionUtils';
import { createBoxAroundPoint } from '@/utils/threejsUtils';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { FACILITY_REQUEST } from '@/utils/http/RequestRegistry';

export const Viewers = {
	//:TODO
	state: {
		view1Type: '2d',
		view2Type: '',
		view3Type: '',
		view4Type: '',

		isView1Visible: true,
		isView2Visible: false,
		isView3Visible: false,
		isView4Visible: false,

		adminModelGeometryLoadedCallback: null,

		compareModeImage: null,
		isLeftMenuPinned: false,
		showNavMenu: true,

		pageView3d: null,
		pageView2d: null,

		taskViewerColoringOption: 'default',

		dbIdsToLoad: [],

		isCropping3D: false,

		// APS Region state for current model/plan
		// These are populated from API responses - no hardcoded defaults
		currentModelRegion: null,
		currentModelStreamingApi: null,
		currentPlanRegion: null,
		currentPlanStreamingApi: null,

		// Pending model load context - used to pass data across the race condition
		// when openViewPageWithType('3d') triggers a watcher before streamingApi can be passed
		pendingModelLoad: null,  // { urn, streamingApi, area }

		// Panorama preservation: which slot (1-4) has a backgrounded 360 viewer, or null
		panoramaPreservedInSlot: null,
	},

	mutations: {
		SET_VIEW_1_TYPE(state, value) {
			if (value == state.view1Type) return;
			if (state.view1Type == '360') {
				if (state.panoramaPreservedInSlot !== 1) {
					store.dispatch('DISPOSE_PANORAMA_VIEWER');
				}
			}
			state.view1Type = value;
		},
		SET_VIEW_2_TYPE(state, value) {
			if (value == state.view2Type) return;
			if (state.view2Type == '360') {
				if (state.panoramaPreservedInSlot !== 2) {
					store.dispatch('DISPOSE_PANORAMA_VIEWER');
				}
			}
			state.view2Type = value;
		},
		SET_VIEW_3_TYPE(state, value) {
			if (value == state.view3Type) return;
			if (state.view3Type == '360') {
				if (state.panoramaPreservedInSlot !== 3) {
					store.dispatch('DISPOSE_PANORAMA_VIEWER');
				}
			}
			state.view3Type = value;
		},
		SET_VIEW_4_TYPE(state, value) {
			if (value == state.view4Type) return;
			if (state.view4Type == '360') {
				if (state.panoramaPreservedInSlot !== 4) {
					store.dispatch('DISPOSE_PANORAMA_VIEWER');
				}
			}
			state.view4Type = value;
		},
		SET_IS_VIEW_1_VISIBLE(state, value) {
			state.isView1Visible = value;
		},
		SET_IS_VIEW_2_VISIBLE(state, value) {
			state.isView2Visible = value;
		},
		SET_IS_VIEW_3_VISIBLE(state, value) {
			state.isView3Visible = value;
		},
		SET_IS_VIEW_4_VISIBLE(state, value) {
			state.isView4Visible = value;
		},
		SET_COMPARE_MODE_IMAGE(state, value) {
			state.compareModeImage = value;
		},
		SET_ADMIN_MODEL_GEOMETRY_LOADED_CALLBACK(state, value) {
			state.adminModelGeometryLoadedCallback = value;
		},
		SET_IS_LEFT_MENU_PINNED(state, value) {
			state.isLeftMenuPinned = value;
		},
		SET_SHOW_NAV_MENU(state, value) {
			state.showNavMenu = value;
		},
		SET_PAGE_VIEW_3D(state, value) {
			state.pageView3d = value;
		},
		SET_PAGE_VIEW_2D(state, value) {
			state.pageView2d = value;
		},
		SET_TASK_VIEWER_COLORING_OPTION(state, value) {
			state.taskViewerColoringOption = value;
		},
		SET_DB_IDS_TO_LOAD(state, value) {
			state.dbIdsToLoad = value;
		},
		SET_IS_CROPPING_3D(state, value) {
			state.isCropping3D = value;
		},
		SET_MODEL_REGION(state, { region, streamingApi }) {
			state.currentModelRegion = region;
			state.currentModelStreamingApi = streamingApi || getStreamingApi(region);
		},
		SET_PLAN_REGION(state, { region, streamingApi }) {
			state.currentPlanRegion = region;
			state.currentPlanStreamingApi = streamingApi || getStreamingApi(region);
		},
		SET_PENDING_MODEL_LOAD(state, payload) {
			// Store context { urn, streamingApi, area } for the watcher-triggered viewer init
			state.pendingModelLoad = payload;
		},
		CLEAR_PENDING_MODEL_LOAD(state) {
			state.pendingModelLoad = null;
		},
		PRESERVE_PANORAMA(state, slotIndex) {
			state.panoramaPreservedInSlot = slotIndex;
		},
		CLEAR_PANORAMA_PRESERVED(state) {
			state.panoramaPreservedInSlot = null;
		},
	},
	getters: {
		GET_VIEW_1_TYPE(state) {
			return state.view1Type;
		},
		GET_VIEW_2_TYPE(state) {
			return state.view2Type;
		},
		GET_VIEW_3_TYPE(state) {
			return state.view3Type;
		},
		GET_VIEW_4_TYPE(state) {
			return state.view4Type;
		},
		GET_IS_VIEW_1_VISIBLE(state) {
			return state.isView1Visible;
		},
		GET_IS_VIEW_2_VISIBLE(state) {
			return state.isView2Visible;
		},
		GET_IS_VIEW_3_VISIBLE(state) {
			return state.isView3Visible;
		},
		GET_IS_VIEW_4_VISIBLE(state) {
			return state.isView4Visible;
		},
		GET_COMPARE_MODE_IMAGE(state) {
			return state.compareModeImage;
		},
		GET_IS_COMPARE_MODE_AVAILABLE(state) {
			let result = 0;
			for (let i = 1; i <= 4; i++) {
				if ((state[`view${i}Type`] == "360") || (state[`view${i}Type`] == "3d")) {
					result++;
				}
			}
			return result == 2;
		},
		GET_360_IMAGE_IN_VIEWER(state) {
			for (let i = 1; i <= 4; i++) {
				if (state[`view${i}Type`] === "360") {
					return state[`isView3${i}Visible`] ? store.state.selectedImage : null;
				}
			}
			return null;
		},
		GET_ADMIN_MODEL_GEOMETRY_LOADED_CALLBACK(state) {
			return state.adminModelGeometryLoadedCallback;
		},
		GET_IS_LEFT_MENU_PINNED(state) {
			return state.isLeftMenuPinned;
		},
		GET_SHOW_NAV_MENU(state) {
			return state.showNavMenu;
		},
		GET_PAGE_VIEW_3D(state) {
			return state.pageView3d;
		},
		GET_PAGE_VIEW_2D(state) {
			return state.pageView2d;
		},
		GET_TASK_VIEWER_COLORING_OPTION(state) {
			return state.taskViewerColoringOption;
		},
		GET_DB_IDS_TO_LOAD(state) {
			return state.dbIdsToLoad;
		},
		GET_IS_CROPPING_3D(state) {
			return state.isCropping3D;
		},
		GET_PAGE_WITH_TYPE_3D(state) {
			return state.view1Type === '3d' || state.view2Type === '3d' || state.view3Type === '3d' || state.view4Type === '3d';
		},
		GET_MODEL_REGION(state) {
			return state.currentModelRegion;
		},
		GET_MODEL_STREAMING_API(state) {
			return state.currentModelStreamingApi;
		},
		GET_PLAN_REGION(state) {
			return state.currentPlanRegion;
		},
		GET_PLAN_STREAMING_API(state) {
			return state.currentPlanStreamingApi;
		},
		GET_PENDING_MODEL_LOAD(state) {
			return state.pendingModelLoad;
		},
		GET_PANORAMA_PRESERVED_IN_SLOT(state) {
			return state.panoramaPreservedInSlot;
		},
	},
	actions: {
		GET_MAIN_URN({ commit, getters }) {
			return new Promise((resolve, reject) => {
				// Read cached urn via the root getter (GET_MAIN_URN → state.mainUrn)
				// rather than touching store.state directly. Local const caches the
				// getter read to avoid repeating the lookup and to disambiguate the
				// shared name with this action (Vuex keeps getters and actions in
				// separate maps, so there's no real collision, but the local name
				// reads cleaner).
				const cachedMainUrn = getters.GET_MAIN_URN;

				if (!cachedMainUrn) {
					HttpService.get({
						url: 'api/assets/getMasterUrn',
						params: {
							assetId: router.currentRoute.params.id,
						},
						...FACILITY_REQUEST,
					})
						.then((response) => {
							let model = response.data;
							if (!model?.urn) {
								return reject('Model not found');
							}
							// Store region info for viewer configuration
							commit('SET_MODEL_REGION', {
								region: model.region,
								streamingApi: model.streamingApi
							});
							resolve({
								urn: model.urn,
								region: model.region,
								streamingApi: model.streamingApi
							});
						})
						.catch((err) => {
							debugError('GET_MAIN_URN failed:', err);
							reject(err);
						});
				} else {
					// Cache branch: read module state via getters, not direct state paths.
					// Prior code used `store.state.viewers?.X` (lowercase "viewers") but the
					// module is registered as "Viewers" in store/index.js, so those reads
					// silently returned undefined — letting launchViewer fall back to its
					// default `streamingV2_EU`, which 404s for non-EU facility models.
					resolve({
						urn: cachedMainUrn,
						region: getters.GET_MODEL_REGION,
						streamingApi: getters.GET_MODEL_STREAMING_API
					});
				}
			});
		},
		GET_MAIN_MODEL() {
			return new Promise((resolve, reject) => {
				HttpService.get({
					url: 'api/assets/getMasterModelVersion',
					params: {
						assetId: router.currentRoute.params.id,
					},
				})
					.then((response) => {
						let model = response.data;
						if (!model) return reject('Model not found');
						resolve(model);
					})
					.catch((error) => {
						debugLog(error);
						reject(error);
					});
			});
		},
		GET_MASTER_PLAN_URN({ commit, getters }) {
			return new Promise((resolve, reject) => {
				// Read cached plan urn via the root getter (GET_PLAN_URN → state.planUrn)
				// rather than touching store.state directly. Mirrors the pattern used
				// by GET_MAIN_URN above.
				const cachedPlanUrn = getters.GET_PLAN_URN;

				if (!cachedPlanUrn) {
					HttpService.get({
							url: 'api/assets/getMasterPlanUrn',
							params: {
								assetId: router.currentRoute.params.id,
							},
						})
						.then((response) => {
							const planData = response.data;
							if (!planData) return resolve(null);
							// Store region info for plan viewer configuration
							commit('SET_PLAN_REGION', {
								region: planData.region,
								streamingApi: planData.streamingApi
							});
							resolve({
								urn: planData.urn,
								region: planData.region,
								streamingApi: planData.streamingApi
							});
						})
						.catch((error) => {
							debugLog(error);
							reject(error);
						});
				} else {
					// Cache branch: read module state via getters, not direct state paths.
					// Prior code used `store.state.viewers?.X` (lowercase "viewers") but the
					// module is registered as "Viewers" in store/index.js, so those reads
					// silently returned undefined and callers silently fell back to
					// default streaming APIs on every cache hit.
					resolve({
						urn: cachedPlanUrn,
						region: getters.GET_PLAN_REGION,
						streamingApi: getters.GET_PLAN_STREAMING_API
					});
				}
			});
		},
		RESTORE_2D_VIEWER({ getters }) {
			let storedViewer = getters.GET_PAGE_VIEW_2D;
			if (!storedViewer) return;
			let existingViewer = document.getElementById('forgePlanViewer');
			if (!existingViewer) return;
			let parentNode = existingViewer.parentNode;
			parentNode.replaceChild(storedViewer, existingViewer);
		},
		RESTORE_3D_VIEWER({ getters }) {
			let storedViewer = getters.GET_PAGE_VIEW_3D;
			if (!storedViewer) return;
			let existingViewer = document.getElementById('forgeViewer');
			if (!existingViewer) return;
			let parentNode = existingViewer.parentNode;
			parentNode.replaceChild(storedViewer, existingViewer);
		},
		RESTORE_3D_VIEWER_TO_TEMP({ getters }) {
			let storedViewer = getters.GET_PAGE_VIEW_3D;
			if (!storedViewer) return;
			let viewerWrapper = document.getElementById('viewer3d-temp');
			if (!viewerWrapper) return;
			if (viewerWrapper?.firstChild) {
				viewerWrapper.insertBefore(storedViewer, viewerWrapper.firstChild);
			} else {
				viewerWrapper.appendChild(storedViewer);
			}
		},
		START_VIEWER_2D({ dispatch, getters, state }, payload) {
			return new Promise((resolve, reject) => {
				try {
					// Handle both legacy string format and new object format
					let urn, streamingApi;
					if (typeof payload === 'string') {
						// Legacy: just URN string - use stored plan region or derive from region
						urn = payload;
						streamingApi = state.currentPlanStreamingApi || getStreamingApi(state.currentPlanRegion);
					} else {
						// New: object with urn and streamingApi
						urn = payload?.urn;
						streamingApi = payload?.streamingApi || state.currentPlanStreamingApi || getStreamingApi(state.currentPlanRegion);
					}

					let currentUrn = store.state.planUrn;
					if (urn === currentUrn && getters.GET_PAGE_VIEW_2D) {
						dispatch('RESTORE_2D_VIEWER');
						return resolve(false);
					} else {
						launchPlanViewer(urn, null, streamingApi)
							.then(() => {
								resolve(true);
							})
							.catch(reject);
					}
				} catch (error) {
					debugLog(error);
					reject(error);
				}
			});
		},
		START_VIEWER_3D({ dispatch, getters }, { area = null, force = false, customUrn = null, customStreamingApi = null, pointCloudOnly = false } = {}) {
			return new Promise((resolve, reject) => {
				const modelRequest = customUrn
					? Promise.resolve({ urn: customUrn, streamingApi: customStreamingApi || 'streamingV2_EU' })
					: dispatch('GET_MAIN_URN');

				modelRequest
					.then((modelData) => {
						const urn = modelData.urn;
						const streamingApi = customStreamingApi || modelData.streamingApi;

						if (urn == store.state.currentUrn && getters.GET_PAGE_VIEW_3D && !force) {
							dispatch('RESTORE_3D_VIEWER');
							resolve(false);
						} else {
							let boundaryArray = area
								? (Array.isArray(area)
									? area.map((a) => a.boundaryArray)
									: area.boundaryArray)
								: null;

							launchViewer(urn, true, boundaryArray, streamingApi, { pointCloudOnly }).then(() => resolve(true)).catch(reject);
						}
					})
					.catch((err) => {
						debugError('START_VIEWER_3D failed:', err);
						reject(err);
					});
			});
		},
		DISPOSE_3D_VIEWER() {
			let viewer = store.state.viewer;
			if (!viewer) return;

			dispose3DViewer();
			store.commit('setIsGeometryLoaded', false);
			store.commit('setIsTreeLoaded', false);
			store.commit('SET_PAGE_VIEW_3D', null);
		},
		OPEN_ADMIN_3D_VIEWER() {
			return new Promise((resolve, reject) => {
				let viewer = store.state.viewer;

				if (viewer) {
					return resolve(viewer);
				}

				let c = store.state.adminAppContainer;

				if (!c) return reject('Admin container not found');

				const callback = () => {
					store.commit('SET_ADMIN_MODEL_GEOMETRY_LOADED_CALLBACK', null);
					resolve(store.state.viewer);
				};

				store.commit('SET_ADMIN_MODEL_GEOMETRY_LOADED_CALLBACK', callback);

				c.open3dViewerIfNot();
			});
		},
		DISPOSE_PANORAMA_VIEWER() {
			if (store.state.forgeService?.panoramaViewer) {
				store.state.forgeService.panoramaViewer.destroy();
				store.state.forgeService.panoramaViewer = null;
				store.state.selectedImage = null;
				store.commit('CLEAR_PANORAMA_PRESERVED');
			}
		},
		OPEN_360_CORRESPONDING_PC(context, { selectedImage, useAnalyzerPriority = false }) {
			return new Promise((resolve, reject) => {
				if (!selectedImage) {
					return reject('No image selected');
				}

				// Save 360 look direction before any 3D viewer changes
				const panoViewer = store.state.forgeService?.panoramaViewer;
				const savedYaw = panoViewer?.getYaw();
				const savedPitch = panoViewer?.getPitch();
				const savedHfov = panoViewer?.getHfov();
				debugLog('[OPEN_360_CORRESPONDING_PC] Saved 360 look: yaw=' + savedYaw?.toFixed(2) + ' pitch=' + savedPitch?.toFixed(2) + ' hfov=' + savedHfov?.toFixed(2));

				// Restores 360 look direction with two-phase approach:
				// 1. Suppress updatePanoViewerFromForge + restore immediately (no flash)
				// 2. Listen for CAMERA_CHANGE_EVENT to settle, then lift suppression
				// viewerRef: pass a captured viewer reference (for the new-viewer path
				// where store.state.viewer is nulled by disposal before we get here).
				const restorePanoLook = (viewerRef) => {
					if (!panoViewer || savedYaw === undefined) {
						debugLog('[OPEN_360_CORRESPONDING_PC] Skip restore: panoViewer=' + !!panoViewer + ' savedYaw=' + savedYaw);
						return;
					}

					// Phase 1: Suppress pano sync and restore immediately (prevents flash)
					store.state.forgeService._suppressPanoSync = true;
					const beforeYaw = panoViewer.getYaw();
					const beforePitch = panoViewer.getPitch();
					debugLog('[OPEN_360_CORRESPONDING_PC] Immediate restore + suppress: before yaw=' + beforeYaw?.toFixed(2) + ' pitch=' + beforePitch?.toFixed(2) + ' → saved yaw=' + savedYaw?.toFixed(2) + ' pitch=' + savedPitch?.toFixed(2));
					panoViewer.lookAt(savedPitch, savedYaw, savedHfov, 0);

					// Phase 2: Wait for camera events to settle, then lift suppression
					const viewer = viewerRef || store.state.viewer;
					let settled = false;

					const onSettled = (source) => {
						if (settled) return;
						settled = true;
						// Final restore to guarantee correct direction
						panoViewer.lookAt(savedPitch, savedYaw, savedHfov, 0);
						store.state.forgeService._suppressPanoSync = false;
						debugLog('[OPEN_360_CORRESPONDING_PC] Suppression lifted (' + source + ')');
					};

					if (viewer) {
						let settleTimer = null;
						const onCameraChange = () => {
							clearTimeout(settleTimer);
							settleTimer = setTimeout(() => {
								try { viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange); } catch (e) { /* viewer may be disposed */ }
								onSettled('camera-settled');
							}, 150);
						};

						try {
							viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
							debugLog('[OPEN_360_CORRESPONDING_PC] Listening for camera settle events');
						} catch (e) {
							onSettled('listener-failed');
							return;
						}

						// Fallback: lift suppression after 1s even if no events
						setTimeout(() => {
							clearTimeout(settleTimer);
							try { viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange); } catch (e) { /* viewer may be disposed */ }
							onSettled('fallback-timeout');
						}, 1000);
					} else {
						onSettled('no-viewer');
					}
				};

				let correspondingPC = null;
				let source = null;

				if (useAnalyzerPriority) {
					// Analyzer Priority: Project PC → Image PC → Opened PC
					// Priority 1: Parent project point cloud (for consistent analysis)
					const parentProject = store.state.projects?.find(p =>
						p.images.some(img => img.id === selectedImage.id)
					);

					if (parentProject) {
						const projectPC = parentProject.projectPointClouds;
						if (projectPC) {
							correspondingPC = projectPC;
							source = 'project';
							debugLog('[OPEN_360_CORRESPONDING_PC] [ANALYZER] Found PC from parent project:', parentProject.name);
						}
					}

					// Priority 2: Image's own point cloud (fallback)
					if (!correspondingPC) {
						const imagePC = selectedImage.scanPointClouds;
						if (imagePC) {
							correspondingPC = imagePC;
							source = '360-image';
							debugLog('[OPEN_360_CORRESPONDING_PC] [ANALYZER] Found PC from selected 360 image');
						}
					}
				} else {
					// View Priority: Image PC → Project PC → Opened PC
					// Priority 1: Image's own point cloud (for viewing specific scan)
					const imagePC = selectedImage.scanPointClouds;
					if (imagePC) {
						correspondingPC = imagePC;
						source = '360-image';
						debugLog('[OPEN_360_CORRESPONDING_PC] [VIEW] Found PC from selected 360 image');
					}

					// Priority 2: Parent project point cloud (fallback)
					if (!correspondingPC) {
						const parentProject = store.state.projects?.find(p =>
							p.images.some(img => img.id === selectedImage.id)
						);

						if (parentProject) {
							const projectPC = parentProject.projectPointClouds;
							if (projectPC) {
								correspondingPC = projectPC;
								source = 'project';
								debugLog('[OPEN_360_CORRESPONDING_PC] [VIEW] Found PC from parent project:', parentProject.name);
							}
						}
					}
				}

				// Priority 3: Any opened point cloud (same for both)
				if (!correspondingPC) {
					const openedPC = store.getters['getPointClouds'].find(pc => pc.isShown === true);
					if (openedPC) {
						correspondingPC = openedPC;
						source = 'opened';
						debugLog('[OPEN_360_CORRESPONDING_PC] Found opened point cloud');
					}
				}

				// No point cloud found
				if (!correspondingPC) {
					return reject('No point cloud available. Please ensure a point cloud is loaded or the image is within a project boundary.');
				}

				let alreadyOpened = store.state.forgeService.pointCloudsToShowIn3D.some((p) => p.name == correspondingPC.name) ||
					store.state.pointClouds.some((pc) => pc.isShown && pc.name == correspondingPC.name);

				const onPcLoaded = () => {
					// Set up bounding box if needed
					try {
						let box = createBoxAroundPoint(selectedImage.getCameraPositionInViewer(), 10);
						let pc = store.state.forgeService.getMainExtension().getPointCloudByName(correspondingPC.name);
						if (pc && box) {
							// Potree.Global.boundingBoxStrict = box;
							// pc.setClippingBoxNew(box);
							// this.pointCloudSettings.pointBudget = 1e7;
							// this.pointCloudSettings.pointSize = 10;
						}
					} catch (err) {
						debugWarn('Could not set up point cloud bounding box:', err);
					}
					resolve({
						pcName: alreadyOpened ? '' : correspondingPC.name,
						wasAlreadyOpened: alreadyOpened,
						// '360-image' | 'project' | 'opened' - 'opened' is a cloud that was already
						// open, not this scan's own (pano360Pick reads it).
						source,
					});
				};

				let viewerExist = !!document.getElementById('forgeViewer');

				if (!store.state.viewer && !viewerExist) {
					store.state.forgeService.image360ToNavigateToIn3D = selectedImage;
					store.state.forgeService.pointCloudsToShowIn3D.push(correspondingPC);

					store.commit('setIsCorrespondingPcLoading', true);

					// Get the main app and open 3D viewer
					let mainApp = store.state.mainApp;
					if (mainApp) {
						mainApp.openViewPageWithType('3d');

						store.state.forgeService.on3dModelLoadedCallback = () => {
							debugLog('[OPEN_360_CORRESPONDING_PC] on3dModelLoadedCallback fired — new viewer path');

							// Capture viewer ref BEFORE disposal (close_viewer nulls store.state.viewer)
							const viewerRef = store.state.viewer;

							// Set up suppress + camera-settle BEFORE resolving promise,
							// so downstream code runs with suppression already active
							debugLog('[OPEN_360_CORRESPONDING_PC] Setting up camera-settle restore (new viewer path)');
							restorePanoLook(viewerRef);

							onPcLoaded();

							// Get view page and handle cleanup
							const { getViewPage3D } = require('@/scripts/js/forgeviewer');
							let page = getViewPage3D();
							if (page) {
								page.move3dToTemp();
								page.$emit('close_viewer', '3d');
							}

							debugLog('[OPEN_360_CORRESPONDING_PC] 3d model loaded, cleanup complete');
							store.commit('setIsCorrespondingPcLoading', false);
						};
					} else {
						reject('Main app not available');
					}
				} else {
					if (!correspondingPC.isShown) {
						debugLog('[OPEN_360_CORRESPONDING_PC] Toggling PC on (existing viewer path), PC:', correspondingPC.name);
						store.state.forgeService.toggleShowingPointCloudIn3D(correspondingPC).then(() => {
							debugLog('[OPEN_360_CORRESPONDING_PC] PC toggled on, setting up camera-settle restore (existing viewer path)');
							restorePanoLook();
							onPcLoaded();
						}).catch(reject);
					} else {
						onPcLoaded();
					}
				}
			});
		},
	},

}
